import {
  ApolloClient,
  gql,
  NormalizedCacheObject,
  useMutation,
  useQuery,
} from '@apollo/client';
import {Mutation, Query} from '../types/generated/graphql';

/**
 * Enqueue a pipeline eval as a background job. Returns the run id
 * immediately (no LLM work happens synchronously) — the eval-lab
 * panel then polls `getPipelineEvalRun` until the worker flips the
 * doc's status to COMPLETED / FAILED.
 */
const MUTATION_ADMIN_RUN_PIPELINE_EVAL = gql`
  mutation adminRunPipelineEval($input: AdminEvalPipelineInput!) {
    adminRunPipelineEval(input: $input) {
      evalRunId
      jobId
    }
  }
`;

export const useAdminRunPipelineEval = (
  client: ApolloClient<NormalizedCacheObject>,
) => {
  type T = Pick<Mutation, 'adminRunPipelineEval'>;
  return useMutation<T>(MUTATION_ADMIN_RUN_PIPELINE_EVAL, {
    client,
  });
};

/**
 * Live status + result for a pipeline-eval run. The panel polls this
 * every 2s while the run's status is PENDING/RUNNING and stops on
 * COMPLETED/FAILED (terminal). The full result shape is returned
 * every call so the UI can render progressively without stitching
 * together multiple queries.
 */
const QUERY_GET_PIPELINE_EVAL_RUN = gql`
  query getPipelineEvalRun($evalRunId: ID!) {
    getPipelineEvalRun(evalRunId: $evalRunId) {
      evalRunId
      videoId
      status
      progress {
        phase
        completed
        total
        note
        updatedAt
      }
      error
      enqueuedAt
      startedAt
      finishedAt
      summary
      totalCostUSD
      totalTokens
      triage {
        model
        verdict
        confidence
        categoryTags
        riskFlags
        score
        rating
        explanation
        tokenUsage { input output total }
        costEstimate { totalCost }
      }
      specialists {
        specialization
        agentVersion
        score
        rating
        explanation
        model
        tokenUsage { input output total }
        costEstimate { totalCost }
        religionFacts { detected faith mode severity }
        politicsFacts { detected stance mode severity }
      }
      skippedSpecialists { specialization reason }
      failedSpecialists { specialization error }
      finalize {
        model
        score
        rating
        explanation
        ageBandVerdicts { band minAge maxAge score rating explanation }
        tokenUsage { input output total }
        costEstimate { totalCost }
      }
    }
  }
`;

export const useGetPipelineEvalRun = (
  client: ApolloClient<NormalizedCacheObject>,
  evalRunId: string | null,
) => {
  type T = Pick<Query, 'getPipelineEvalRun'>;
  const {data, loading, error, startPolling, stopPolling} = useQuery<T>(
    QUERY_GET_PIPELINE_EVAL_RUN,
    {
      client,
      variables: {evalRunId: evalRunId ?? ''},
      skip: !evalRunId,
      fetchPolicy: 'network-only',
    },
  );
  return {data, loading, error, startPolling, stopPolling};
};
